import { useState, type ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  buildRfpEmailContent,
  contentForRecipient,
  defaultRespondBy,
  messageFor,
  NOT_SPECIFIED,
} from "@shared/utils/rfpEmailContent";
import { useAuth } from "@/hooks/useAuth";
import type { RfpEmailRecipient } from "@/lib/quoting/rfpRecipients";
import {
  agencyCopyRecipients,
  buildOutgoingEmails,
  emailsWithNoRecipient,
} from "@/lib/quoting/outgoingEmails";
import { toneFor } from "@/lib/quoting/gaTone";
import { GaMark } from "@/lib/quoting/GaMark";
import { cn } from "@/lib/utils";
import { getPlanTypeAccent, planTypeRank } from "@shared/schemas/planTypes";
import { normalizePlanType } from "@shared/schema";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  Building2,
  Calendar,
  Users,
  Plus,
  X,
  UserPlus,
  Phone,
  Loader2,
  Pencil,
  UserCheck,
  AlertCircle,
  // Marks which card the right-hand preview is showing, so the two columns
  // agree about which email is on screen.
  Eye,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import type { Carrier } from "@shared/schema";
import type { HealthRFPParameters } from "./HealthRFPForm";
import type { DentalRFPParameters } from "./DentalRFPForm";
import type { VisionRFPParameters } from "./VisionRFPForm";
import type { LifeRFPParameters } from "./LifeRFPForm";
import type { DisabilityRFPParameters } from "./DisabilityRFPForm";
import type { VoluntaryRFPParameters } from "./VoluntaryRFPForm";

// Shared with Step7RFPPreview, which builds these. Both used to declare their
// own copy, and neither carried generalAgencyId.
type AvailableContact = RfpEmailRecipient;

// Keyed on the labels buildRfpEmailContent produces. Anything unrecognised
// still renders, just with the neutral icon.
interface ConsolidatedRFPEmailProps {
  availableContacts: AvailableContact[];
  selectedContactIds: Set<string>;
  onToggleContact: (id: string) => void;
  customRecipients: AvailableContact[];
  onAddCustomRecipient: (recipient: {
    name: string;
    email: string;
    carrierId?: number;
    carrierName?: string;
    directoryContactId?: number;
  }) => void;
  onRemoveCustomRecipient: (id: string) => void;
  onContactCreated?: () => void;
  carriers: Carrier[];
  /** Carriers chosen for this RFP — offered first when adding a contact. */
  rfpCarriers: Carrier[];
  /** Chosen carriers with no sales contact on file, so nobody to email. */
  carriersWithoutContacts: Carrier[];
  /**
   * Sending through a general agent rather than to each carrier. The body then
   * names the carriers to quote, since a GA has to be told which markets to go
   * to. The server derives the same list from the quote's wizardState in
   * rfpProposalRoutes.ts — keep the two in step.
   */
  usingGa?: boolean;
  /**
   * What each recipient's email will name, by recipient id. Undefined means no
   * carrier list at all, which carrier mode must always be: naming the picked
   * carriers to a carrier hands it the list of who else is bidding.
   */
  carrierNamesByRecipient?: Map<string, string[] | undefined>;
  /**
   * The attachments editor, rendered as the last row of the envelope block
   * alongside From, Reply to, Subject and To. A slot and not a file list: the
   * parent owns the query and the upload, and the preview used to fake it with
   * an "Attached: a.pdf" line of body text the recipient never sees.
   */
  attachmentsSlot?: ReactNode;
  /** The agency's provisioned sending identity — carriers see this. */
  senderName?: string | null;
  senderEmail?: string | null;
  /** Where carrier replies go. Editable per send. */
  replyTo: string;
  onReplyToChange: (value: string) => void;
  /** Optional note for one recipient, replacing the shared message for them. */
  perRecipientMessages: Record<string, string>;
  onPerRecipientMessageChange: (recipientId: string, value: string) => void;
  coverageTypes: string[];
  rfpParameters: Record<string, HealthRFPParameters | DentalRFPParameters | VisionRFPParameters | LifeRFPParameters | DisabilityRFPParameters | VoluntaryRFPParameters>;
  companyName: string;
  effectiveDate: Date;
  employeeCount: number;
  customMessage: string;
  onCustomMessageChange: (message: string) => void;
  /**
   * The parts of the email the broker may rewrite, in place behind "Edit
   * message". One set for the whole RFP, not per firm: wording only one firm
   * sees goes in the per-recipient note, which already does exactly that.
   *
   * Prose only. The facts, the carrier list, the coverage sections and the
   * recipient's own name are per recipient — a GA is asked for the carriers it
   * can place and a carrier sees only its own lines — and one block of
   * typed-over text cannot express that. emailGreeting is therefore the
   * salutation WORD ("Dear"), never the whole greeting.
   */
  respondBy: string | null;
  emailGreeting: string | null;
  emailIntro: string | null;
  emailClosing: string | null;
  emailSignature: string | null;
  onTemplateChange: (patch: {
    respondBy?: string | null;
    emailGreeting?: string | null;
    emailIntro?: string | null;
    emailClosing?: string | null;
    emailSignature?: string | null;
  }) => void;
  /** Who the RFP is from, for the default sign-off. */
  sender?: {
    name: string | null;
    email: string | null;
    phone: string | null;
    agencyName: string | null;
    agencyLicenseNumber: string | null;
  } | null;
  /**
   * A data: URI. Shown straight in the preview, which is a browser; the send
   * decodes it into an embedded attachment, because mail clients strip these.
   */
  agencyLogo?: string | null;
  /**
   * Raised while the message is being edited. The footer's Send button lives in
   * the step, not in here, and would otherwise send the wording from BEFORE the
   * edit while the broker is still typing it, with nothing to say so.
   */
  onEditingMessageChange?: (editing: boolean) => void;
}

const getCoverageLabel = (type: string) => {
  const labels: Record<string, string> = {
    medical: "Medical",
    dental: "Dental",
    vision: "Vision",
    life: "Life Insurance",
    disability: "Disability Insurance",
    voluntary: "Voluntary Benefits",
  };
  return labels[type] || type;
};

const VOLUNTARY_BENEFIT_LABELS: Record<string, string> = {
  accident: "Accident Insurance",
  critical_illness: "Critical Illness Insurance",
  cancer: "Cancer Insurance",
  hospital_indemnity: "Hospital Indemnity",
  gap: "Gap Insurance",
  pet: "Pet Insurance",
  legal: "Legal Services",
  identity_theft: "Identity Theft Protection",
  whole_life: "Whole Life Insurance",
  universal_life: "Universal Life Insurance",
};

export function ConsolidatedRFPEmail({
  availableContacts,
  selectedContactIds,
  onToggleContact,
  customRecipients,
  onAddCustomRecipient,
  onRemoveCustomRecipient,
  onContactCreated,
  carriers,
  rfpCarriers,
  usingGa,
  carrierNamesByRecipient,
  attachmentsSlot,
  carriersWithoutContacts,
  senderName,
  senderEmail,
  replyTo,
  onReplyToChange,
  perRecipientMessages,
  onPerRecipientMessageChange,
  coverageTypes,
  rfpParameters,
  companyName,
  effectiveDate,
  employeeCount,
  customMessage,
  onCustomMessageChange,
  respondBy,
  emailGreeting,
  emailIntro,
  emailClosing,
  emailSignature,
  sender,
  agencyLogo,
  onTemplateChange,
  onEditingMessageChange,
}: ConsolidatedRFPEmailProps) {
  const { toast } = useToast();
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", carrierId: "" });
  const [addContactTab, setAddContactTab] = useState<"agency" | "new">("agency");
  const { user } = useAuth();
  // Held as a draft so Cancel can put it back.
  const [editingTemplate, setEditingTemplate] = useState(false);
  // Every enter and exit goes through here, so the step's Send button can never
  // be left armed over an open editor.
  const setEditing = (editing: boolean) => {
    setEditingTemplate(editing);
    onEditingMessageChange?.(editing);
  };
  const [templateDraft, setTemplateDraft] = useState({
    respondBy: "",
    greeting: "",
    intro: "",
    closing: "",
    signature: "",
  });
  // Our wording as it stood when the editor opened, snapshotted rather than
  // recomputed. Done decides "typed over" vs "left as ours" by comparing
  // against this; recomputed each render it follows the previewed recipient,
  // so clicking another card mid-edit could save untouched text as an edit.
  const [templateBasis, setTemplateBasis] = useState({
    respondBy: "",
    greeting: "",
    intro: "",
    closing: "",
    signature: "",
  });

  // Fetch agency team members for CC option
  const { data: allAgencyTeamMembers = [] } = useQuery<Array<{
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    role: string;
  }>>({
    queryKey: ["/api/agency/team-members"],
    enabled: addContactOpen,
  });

  // Not yourself: you are the sender, and the reply-to is already your address,
  // so CC'ing yourself mails you a copy of your own email. The endpoint keeps
  // returning everyone — other screens list the whole team on purpose.
  const agencyTeamMembers = allAgencyTeamMembers.filter(
    (member) =>
      member.id !== (user as any)?.id &&
      member.email?.toLowerCase() !== (user as any)?.email?.toLowerCase(),
  );

  // Fetch all directory contacts for deduplication check
  const { data: allDirectoryContacts = [] } = useQuery<Array<{
    id: number;
    email: string | null;
    contactName: string | null;
  }>>({
    queryKey: ["/api/directory-contacts"],
    enabled: addContactOpen,
  });

  // One draft per carrier still missing a contact.
  const [missingCarrierDrafts, setMissingCarrierDrafts] = useState<
    Record<number, { name: string; email: string }>
  >({});
  const [savingCarrierId, setSavingCarrierId] = useState<number | null>(null);

  // Inline correction of a directory contact from the recipient list.
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [contactDraft, setContactDraft] = useState<{ name: string; email: string } | null>(null);
  const [savingContactId, setSavingContactId] = useState<string | null>(null);

  const handleSaveContactEdit = async (
    contact: AvailableContact,
    draft: { name: string; email: string },
  ) => {
    if (!contact.directoryContactId) return;
    setSavingContactId(contact.id);
    try {
      const response = await apiRequest("PUT", `/api/directory-contacts/${contact.directoryContactId}`, {
        contactName: draft.name.trim(),
        email: draft.email.trim(),
      });
      if (!response.ok) throw new Error((await response.json())?.message ?? "Could not update the contact");
      setEditingContactId(null);
      setContactDraft(null);
      onContactCreated?.();
      toast({ title: "Contact updated", description: "Saved to your directory." });
    } catch (error: any) {
      toast({ title: "Couldn't update the contact", description: error.message, variant: "destructive" });
    } finally {
      setSavingContactId(null);
    }
  };

  const handleAddCarrierContact = async (
    carrier: Carrier,
    draft: { name: string; email: string },
  ) => {
    setSavingCarrierId(carrier.id);
    try {
      const response = await apiRequest("POST", "/api/directory-contacts", {
        type: "carrier",
        carrierId: carrier.id,
        // `name` is the organisation (NOT NULL on directory_contacts);
        // `contactName` is the person. Matches the Add Contact dialog above.
        name: carrier.name,
        contactName: draft.name.trim(),
        email: draft.email.trim(),
        contactType: "sales",
        isActive: true,
      });
      if (!response.ok) throw new Error((await response.json())?.message ?? "Could not save the contact");
      const directoryContact = (await response.json()) as { id: number };

      onAddCustomRecipient({
        name: draft.name.trim(),
        email: draft.email.trim(),
        directoryContactId: directoryContact.id,
        carrierId: carrier.id,
        carrierName: carrier.name,
      });
      setMissingCarrierDrafts((prev) => {
        const next = { ...prev };
        delete next[carrier.id];
        return next;
      });
      // Bring it in through the directory too, so it's there next time.
      onContactCreated?.();
      toast({ title: `${carrier.name} contact saved`, description: "Added to this RFP and your directory." });
    } catch (error: any) {
      toast({ title: "Couldn't save the contact", description: error.message, variant: "destructive" });
    } finally {
      setSavingCarrierId(null);
    }
  };

  const handleAddAgencyContact = (member: { id: string; firstName: string | null; lastName: string | null; email: string | null }) => {
    if (!member.email) {
      toast({
        title: "No Email",
        description: "This team member doesn't have an email address.",
        variant: "destructive",
      });
      return;
    }

    const name = [member.firstName, member.lastName].filter(Boolean).join(" ") || "Agency Contact";
    
    // Add as custom recipient WITHOUT creating a directory entry
    onAddCustomRecipient({
      name,
      email: member.email,
      carrierName: "Agency",
    });

    setAddContactOpen(false);
    toast({
      title: "Contact Added",
      description: `${name} has been added as a CC recipient.`,
    });
  };

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.email) {
      toast({
        title: "Missing Information",
        description: "Please fill in contact name and email.",
        variant: "destructive",
      });
      return;
    }

    const selectedCarrier = newContact.carrierId
      ? carriers.find((c) => c.id === parseInt(newContact.carrierId))
      : null;

    // Check if this email already exists in directory contacts
    const existingContact = allDirectoryContacts.find(
      (c) => c.email?.toLowerCase() === newContact.email.toLowerCase()
    );

    if (existingContact) {
      // Use existing contact instead of creating duplicate
      onAddCustomRecipient({
        name: existingContact.contactName || newContact.name,
        email: newContact.email,
        directoryContactId: existingContact.id,
        carrierName: selectedCarrier?.name,
      });

      setNewContact({ name: "", email: "", carrierId: "" });
      setAddContactOpen(false);

      toast({
        title: "Contact Added",
        description: `${existingContact.contactName || newContact.name} was found in your directory and added as a recipient.`,
      });
      return;
    }

    setIsCreatingContact(true);

    try {
      // Create directory contact via API
      const response = await apiRequest("POST", "/api/directory-contacts", {
        type: selectedCarrier ? "carrier" : "vendor",
        name: selectedCarrier?.name || newContact.name,
        contactName: newContact.name,
        email: newContact.email,
        contactType: "sales",
        ...(selectedCarrier && { carrierId: selectedCarrier.id }),
        isActive: true,
      });

      const directoryContact = await response.json() as { id: number };

      // Always add it here. The old code skipped this whenever a carrier was
      // picked, assuming the refetch would surface the contact — but
      // availableContacts only contains carriers selected for THIS RFP, so a
      // contact added for any other carrier silently disappeared. Step 7 drops
      // this copy once the same email arrives through the directory, so it can
      // never show twice.
      onAddCustomRecipient({
        name: newContact.name,
        email: newContact.email,
        directoryContactId: directoryContact.id,
        carrierId: selectedCarrier?.id,
        carrierName: selectedCarrier?.name,
      });

      setNewContact({ name: "", email: "", carrierId: "" });
      setAddContactOpen(false);

      // Refetch directory contacts so new contact appears in the main list (if carrier-linked)
      onContactCreated?.();

      toast({
        title: "Contact Added",
        description: selectedCarrier
          ? `${newContact.name} has been added to the directory and will appear in the recipients list.`
          : `${newContact.name} has been added to the directory and selected as a recipient.`,
      });
    } catch (error: any) {
      console.error("Failed to create directory contact:", error);
      toast({
        title: "Failed to Add Contact",
        description: error?.message || "Could not create the contact. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingContact(false);
    }
  };

  // Build recipients list from selected contacts + custom recipients
  const selectedRecipients = availableContacts.filter((c) => selectedContactIds.has(c.id));
  const allRecipients = [...selectedRecipients, ...customRecipients];

  // Step 4 blocks this, but a draft resumed after a GA's carriers changed can
  // still land here, and silently dropping a carrier is the failure to avoid.
  const askedCarrierNames = new Set(
    allRecipients.flatMap((recipient) => carrierNamesByRecipient?.get(recipient.id) ?? []),
  );
  const uncoveredCarrierNames = usingGa
    ? rfpCarriers.map((carrier) => carrier.name).filter((name) => !askedCarrierNames.has(name))
    : [];

  // Which recipient we're previewing. Each carrier is sent only the coverage
  // lines they were asked to quote, so there is no single "the email" — brokers
  // need to check what each one actually receives before it goes out.
  const [previewRecipientId, setPreviewRecipientId] = useState<string | null>(null);
  // Whether the notes box is editing the shared message or this carrier's own.
  const [messageScope, setMessageScope] = useState<"shared" | "recipient">("shared");

  // What is actually being reviewed: one email per firm, not one per person.
  // Two contacts at a GA are two copies of the same email, which the old flat
  // list could not express, and which gave the preview two identical tabs.
  // One recipient row: tick, inline-edit, or drop from this RFP. Pulled out
  // of the old flat list so each outgoing email can render its own people.
  const renderContactRow = (contact: AvailableContact) => {

                const isSelected = selectedContactIds.has(contact.id);
                if (editingContactId === contact.id) {
                  const draft = contactDraft ?? { name: contact.name, email: contact.email };
                  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim());
                  return (
                    <div key={contact.id} className="flex items-center gap-2 px-2.5 py-1.5 border rounded-md">
                      <span className="text-xs text-muted-foreground w-24 shrink-0 truncate">
                        {contact.carrierName}
                      </span>
                      <Input
                        className="h-8 text-xs"
                        value={draft.name}
                        placeholder="Contact name"
                        onChange={(e) => setContactDraft({ ...draft, name: e.target.value })}
                      />
                      <Input
                        className="h-8 text-xs"
                        value={draft.email}
                        placeholder="sales@carrier.com"
                        onChange={(e) => setContactDraft({ ...draft, email: e.target.value })}
                      />
                      <Button
                        size="sm"
                        className="h-7 shrink-0"
                        disabled={!draft.name.trim() || !emailValid || savingContactId === contact.id}
                        onClick={() => handleSaveContactEdit(contact, draft)}
                      >
                        {savingContactId === contact.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          "Save"
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 shrink-0"
                        onClick={() => {
                          setEditingContactId(null);
                          setContactDraft(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  );
                }
                return (
                  <div
                    key={contact.id}
                    className={`flex items-center gap-2.5 px-2.5 py-1.5 border rounded-md cursor-pointer transition-colors ${
                      isSelected ? "bg-primary/5 border-primary" : "hover:bg-muted/50"
                    }`}
                    onClick={() => onToggleContact(contact.id)}
                    title={contact.phone ? `${contact.email} · ${contact.phone}` : contact.email}
                  >
                    <Checkbox checked={isSelected} onCheckedChange={() => onToggleContact(contact.id)} />
                    <span className="text-sm font-medium shrink-0 truncate max-w-[9rem]">{contact.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0 truncate max-w-[8rem]">
                      {contact.carrierName}
                    </span>
                    <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">
                      {contact.email}
                    </span>
                    <div className="flex gap-1 shrink-0">
                      {contact.coverageTypes.map((type) => (
                        <Badge key={type} variant="secondary" className="text-[10px] px-1.5 py-0">
                          {getCoverageLabel(type)}
                        </Badge>
                      ))}
                    </div>
                    {/* Fix a wrong name or address without leaving the step —
                        saves back to the Insurance Directory, so it's corrected
                        everywhere, not just for this RFP. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-foreground"
                      title="Edit this contact"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingContactId(contact.id);
                        setContactDraft({ name: contact.name, email: contact.email });
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    {/* Removing leaves this RFP only; the contact stays in the
                        directory and its carrier reappears above as needing someone. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                      title="Remove from this RFP"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSelected) onToggleContact(contact.id);
                      }}
                      disabled={!isSelected}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
  };

  // A hand-added recipient. Always sent, so no tick: the only action is to drop
  // them again. Kept visually distinct because they have no directory row.
  const renderCustomRow = (recipient: AvailableContact, showBadge = true) => (
    <div
      key={recipient.id}
      className="flex items-center gap-2.5 px-2.5 py-1.5 border rounded-md bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800"
      title={recipient.email}
    >
      <span className="min-w-0 truncate text-xs font-medium">{recipient.name}</span>
      <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
        {recipient.email}
      </span>
      {showBadge && (
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">Custom</Badge>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onRemoveCustomRecipient(recipient.id);
        }}
        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive shrink-0"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );

  // Compact row for inside an email card. The wide list row does not fit a
  // 26rem column, and its carrier name and coverage badges are already said by
  // the card header and "Asked to quote" above it.
  const renderCompactRow = (contact: AvailableContact) => {
    const isSelected = selectedContactIds.has(contact.id);
    if (editingContactId === contact.id) return renderContactRow(contact);
    return (
      <div
        key={contact.id}
        onClick={(e) => {
          e.stopPropagation();
          onToggleContact(contact.id);
        }}
        title={contact.phone ? `${contact.email} · ${contact.phone}` : contact.email}
        className={cn(
          "flex items-center gap-2 rounded-md border px-2 py-1.5 cursor-pointer transition-colors",
          isSelected ? "border-primary/40 bg-primary/5" : "hover:bg-muted/40",
        )}
      >
        <Checkbox checked={isSelected} className="pointer-events-none shrink-0" />
        {/* Both text cells min-w-0, or a long name pushes the buttons out of
            the 26rem column instead of truncating. */}
        <span className="min-w-0 truncate text-xs font-medium">{contact.name}</span>
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
          {contact.email}
        </span>
        {/* Carried over from the wide row: marks who has a note of their own,
            since the note itself only shows in the preview pane. */}
        {perRecipientMessages[contact.id]?.trim() && (
          <span className="shrink-0 text-[11px] text-primary" title="Has its own note">
            ✎
          </span>
        )}
        <button
          type="button"
          title="Edit this contact"
          className="shrink-0 text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            setEditingContactId(contact.id);
            setContactDraft({ name: contact.name, email: contact.email });
          }}
        >
          <Pencil className="h-3 w-3" />
        </button>
        <button
          type="button"
          title="Remove from this RFP"
          disabled={!isSelected}
          className="shrink-0 text-muted-foreground hover:text-destructive disabled:opacity-40"
          onClick={(e) => {
            e.stopPropagation();
            if (isSelected) onToggleContact(contact.id);
          }}
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  };

  const emailsInput = {
    recipients: availableContacts,
    usingGa: !!usingGa,
    selectedIds: selectedContactIds,
    customRecipients,
  };
  const outgoingEmails = buildOutgoingEmails(emailsInput);
  // Colleagues ride along on every email rather than getting one of their own.
  // The send derives the same list with the same predicate.
  const ccRecipients = agencyCopyRecipients(emailsInput);
  const [selectedEmailKey, setSelectedEmailKey] = useState<string | null>(null);
  const selectedEmail =
    outgoingEmails.find((email) => email.key === selectedEmailKey) ?? outgoingEmails[0];

  // Preview follows the selected email. Falling back to its first recipient
  // keeps the greeting sensible when the previewed person is unticked.
  const previewRecipient =
    selectedEmail?.ticked.find((r) => r.id === previewRecipientId) ??
    selectedEmail?.ticked[0] ??
    allRecipients.find((r) => r.id === previewRecipientId) ??
    allRecipients[0];

  // Which firm colours the preview header. Same id the left card uses, so the
  // two never disagree. Undefined for the agency-copy group, which has neither.
  const previewFirmId =
    selectedEmail?.ticked[0]?.generalAgencyId ?? selectedEmail?.ticked[0]?.carrierId;

  const contentInput = {
    coverageTypes,
    rfpParameters,
    companyName,
    effectiveDate,
    employeeCount,
    // This recipient's own list, not the whole picked set: a GA is only asked
    // for the carriers it works with, and the server builds it the same way.
    carrierNames: previewRecipient
      ? carrierNamesByRecipient?.get(previewRecipient.id)
      : undefined,
    // carrierName holds the GA's name in GA mode, which is what the server
    // resolves from the firm itself. Must match, or the subject shown here is
    // not the subject sent.
    recipientLabel: usingGa ? previewRecipient?.carrierName : undefined,
    customMessage: previewRecipient
      ? messageFor(previewRecipient.id, customMessage, perRecipientMessages)
      : customMessage,
    // The same values the server reads back out of wizardState at send time.
    respondBy: respondBy || undefined,
    sender,
  };
  const fullContent = buildRfpEmailContent({
    ...contentInput,
    greeting: emailGreeting,
    intro: emailIntro,
    closing: emailClosing,
    signature: emailSignature,
  });
  // The same email with no rewriting: what "Edit message" starts from, and what
  // a cleared box goes back to.
  const generatedContent = buildRfpEmailContent(contentInput);
  const templateEdited =
    templateDraft.greeting.trim() !== templateBasis.greeting ||
    templateDraft.intro.trim() !== templateBasis.intro ||
    templateDraft.closing.trim() !== templateBasis.closing ||
    templateDraft.signature.trim() !== templateBasis.signature ||
    templateDraft.respondBy !== templateBasis.respondBy;
  // Exactly what this recipient's email will contain.
  const previewContent = contentForRecipient(fullContent, previewRecipient?.coverageTypes);


  return (
    // Master/detail: the emails going out on the left, the selected one on the
    // right. One column below lg, where side-by-side stops being readable.
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] gap-4 items-start">
      {/* Left: one card per outgoing email. No card around the column itself,
          or a border wraps a stack of bordered cards. */}
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
            <p className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Emails going out · click one to preview
            </p>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs">
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                    Add contact
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Add Contact</DialogTitle>
                    <DialogDescription>
                      Add a team member or create a new contact for this RFP.
                    </DialogDescription>
                  </DialogHeader>
                  <Tabs value={addContactTab} onValueChange={(v) => setAddContactTab(v as "agency" | "new")} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="agency">
                        <UserCheck className="h-4 w-4 mr-2" />
                        Agency Team
                      </TabsTrigger>
                      <TabsTrigger value="new">
                        <UserPlus className="h-4 w-4 mr-2" />
                        New Contact
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="agency" className="space-y-4 pt-4">
                      <p className="text-sm text-muted-foreground">
                        Select a team member to CC on this RFP. They will not be added to the Insurance Directory.
                      </p>
                      {agencyTeamMembers.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          Nobody else on your team to copy.
                        </p>
                      ) : (
                        <ScrollArea className="h-[200px] border rounded-md">
                          <div className="p-2 space-y-1">
                            {agencyTeamMembers
                              .filter((m) => m.email)
                              .map((member) => {
                                const name = [member.firstName, member.lastName].filter(Boolean).join(" ") || "Unknown";
                                const isAlreadyAdded = customRecipients.some(
                                  (r) => r.email.toLowerCase() === member.email?.toLowerCase()
                                );
                                return (
                                  <div
                                    key={member.id}
                                    className={`flex items-center justify-between p-2 rounded hover:bg-muted ${isAlreadyAdded ? "opacity-50" : "cursor-pointer"}`}
                                    onClick={() => !isAlreadyAdded && handleAddAgencyContact(member)}
                                  >
                                    <div>
                                      <div className="font-medium text-sm">{name}</div>
                                      <div className="text-xs text-muted-foreground">{member.email}</div>
                                    </div>
                                    {isAlreadyAdded ? (
                                      <Badge variant="secondary" className="text-xs">Added</Badge>
                                    ) : (
                                      <Button variant="ghost" size="sm">
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </ScrollArea>
                      )}
                    </TabsContent>

                    <TabsContent value="new" className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label htmlFor="carrierId">Carrier</Label>
                        <Select
                          value={newContact.carrierId}
                          onValueChange={(value) => setNewContact({ ...newContact, carrierId: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a carrier (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            {/* This RFP's carriers first — the full list of 40+
                                made the relevant ones hard to find. */}
                            {rfpCarriers.length > 0 && (
                              <SelectGroup>
                                <SelectLabel>On this RFP</SelectLabel>
                                {rfpCarriers.map((carrier) => (
                                  <SelectItem key={`rfp-${carrier.id}`} value={String(carrier.id)}>
                                    {carrier.name}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            )}
                            <SelectGroup>
                              <SelectLabel>All carriers</SelectLabel>
                              {carriers
                                .filter((carrier) => !rfpCarriers.some((c) => c.id === carrier.id))
                                .map((carrier) => (
                                  <SelectItem key={carrier.id} value={String(carrier.id)}>
                                    {carrier.name}
                                  </SelectItem>
                                ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactName">Contact Name <span className="text-destructive">*</span></Label>
                        <Input
                          id="contactName"
                          value={newContact.name}
                          onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                          placeholder="John Smith"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contactEmail">Email Address <span className="text-destructive">*</span></Label>
                        <Input
                          id="contactEmail"
                          type="email"
                          value={newContact.email}
                          onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                          placeholder="john@example.com"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        If this email already exists in your directory, we'll use that contact instead of creating a duplicate.
                      </p>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={() => setAddContactOpen(false)} disabled={isCreatingContact}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleAddContact}
                          disabled={!newContact.name || !newContact.email || isCreatingContact}
                        >
                          {isCreatingContact ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Adding...
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-2" />
                              Add Contact
                            </>
                          )}
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </DialogContent>
              </Dialog>
            </div>
        </div>
        <div>
          {/* GA mode keeps only the gap warning here: a carrier list belongs in
              the card for the email that carries it. */}
          {usingGa && uncoveredCarrierNames.length > 0 && (
            <p className="mb-4 flex items-start gap-1.5 rounded-lg border border-amber-300 p-3 text-xs text-amber-700 dark:border-amber-800 dark:text-amber-500">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
              <span>
                Nobody is being asked for {uncoveredCarrierNames.join(", ")}. Go back to step 4
                and tick a GA that works with{" "}
                {uncoveredCarrierNames.length === 1 ? "them" : "those"}.
              </span>
            </p>
          )}

          {/* Carrier mode: how many of the carriers picked in step 4 we can
              actually reach, and a way to fix the rest without leaving the
              step. Through a GA there is nothing to count, since the GA is the
              one approaching the carriers. */}
          {!usingGa && rfpCarriers.length > 0 && (
            <div className="mb-4 rounded-lg border p-3">
              <p className="text-sm font-medium">
                {rfpCarriers.length - carriersWithoutContacts.length} of {rfpCarriers.length} carrier
                {rfpCarriers.length === 1 ? "" : "s"} have a sales contact
              </p>
              {carriersWithoutContacts.length > 0 && (
                <>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                    Add the missing ones and they'll be saved to your directory for next time.
                  </p>
                  {/* Stacked, not one flex row. Carrier name, two inputs and a
                      button side by side left each field about five characters
                      wide in this column — you could not read what you typed. */}
                  <div className="space-y-2">
                    {carriersWithoutContacts.map((carrier) => {
                      const draft = missingCarrierDrafts[carrier.id] ?? { name: "", email: "" };
                      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim());
                      const saving = savingCarrierId === carrier.id;
                      return (
                        <div key={carrier.id} className="space-y-2 rounded-md border p-2.5">
                          <p className="truncate text-xs font-semibold" title={carrier.name}>
                            {carrier.name}
                          </p>
                          <Input
                            className="h-8 text-sm"
                            placeholder="Contact name"
                            value={draft.name}
                            onChange={(e) =>
                              setMissingCarrierDrafts((prev) => ({
                                ...prev,
                                [carrier.id]: { ...draft, name: e.target.value },
                              }))
                            }
                          />
                          <Input
                            className="h-8 font-mono text-sm"
                            placeholder="sales@carrier.com"
                            value={draft.email}
                            // Enter saves: with the button below the fields,
                            // reaching for the mouse to finish a two-field form
                            // is the slow way round.
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && draft.name.trim() && emailValid && !saving) {
                                handleAddCarrierContact(carrier, draft);
                              }
                            }}
                            onChange={(e) =>
                              setMissingCarrierDrafts((prev) => ({
                                ...prev,
                                [carrier.id]: { ...draft, email: e.target.value },
                              }))
                            }
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-full text-xs"
                            disabled={!draft.name.trim() || !emailValid || saving}
                            onClick={() => handleAddCarrierContact(carrier, draft)}
                          >
                            {saving ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              `Add contact at ${carrier.name.split(" ")[0]}`
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {availableContacts.length === 0 && customRecipients.length === 0 ? (
            <div className="text-sm text-center py-6 space-y-2">
              <p className="text-muted-foreground">
                {carriersWithoutContacts.length
                  ? `No sales contact on file for ${carriersWithoutContacts.map((c) => c.name).join(", ")}.`
                  : usingGa
                    ? "Add anyone else who should receive this RFP."
                    : "No sales contacts found for the carriers on this RFP."}
              </p>
              <p className="text-muted-foreground text-xs">
                Add one with the "Add contact" button — it's saved to your directory, so you only
                do this once per carrier.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {/* Warns, does not block: the send rules are recipients, a
                  provisioned sender, a valid reply-to, and nothing else. A GA
                  with nobody ticked is simply left out, which is easy to miss
                  when its card is scrolled off. */}
              {emailsWithNoRecipient(outgoingEmails).length > 0 && (
                <p className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
                  {emailsWithNoRecipient(outgoingEmails)
                    .map((email) => email.title)
                    .join(", ")}{" "}
                  {emailsWithNoRecipient(outgoingEmails).length === 1 ? "has" : "have"} nobody
                  ticked, so {emailsWithNoRecipient(outgoingEmails).length === 1 ? "it" : "they"}{" "}
                  will not be sent.
                </p>
              )}

              {/* One card per outgoing email. Selecting one drives the preview,
                  which is why contacts live in here rather than in a flat list:
                  two people at a GA are two copies of the same email. */}
              {outgoingEmails.map((email) => {
                const isSelected = selectedEmail?.key === email.key;
                const goingNowhere = email.ticked.length === 0;
                const firmId = email.ticked[0]?.generalAgencyId ?? email.ticked[0]?.carrierId;
                const tone = firmId ? toneFor(firmId) : null;
                const carrierNames = email.ticked.length
                  ? carrierNamesByRecipient?.get(email.ticked[0].id) ?? []
                  : [];
                // Group the names back into their coverage lines so the card
                // reads the way the email does.
                const askedByLine = coverageTypes
                  .slice()
                  .sort((a, b) => planTypeRank(a) - planTypeRank(b))
                  .map((coverageType) => ({
                    coverageType,
                    names: carrierNames.filter((name) =>
                      rfpCarriers.some(
                        (carrier) =>
                          carrier.name === name &&
                          (carrier.types ?? []).some(
                            (t) => normalizePlanType(t) === normalizePlanType(coverageType),
                          ),
                      ),
                    ),
                  }))
                  .filter((line) => line.names.length > 0);
                // A carrier with no plan types on file matches no line above and
                // would vanish from the card, which would understate what the GA
                // is being asked for. Kept in an untagged row instead.
                const untaggedNames = carrierNames.filter(
                  (name) => !askedByLine.some((line) => line.names.includes(name)),
                );

                return (
                  <div
                    key={email.key}
                    onClick={() => {
                      setSelectedEmailKey(email.key);
                      setPreviewRecipientId(null);
                    }}
                    className={cn(
                      "cursor-pointer overflow-hidden rounded-xl border transition-colors",
                      isSelected && tone
                        ? `${tone.border} ring-2 ${tone.ring}`
                        : isSelected
                          ? "border-primary ring-2 ring-primary/15"
                          : goingNowhere
                            ? "border-amber-300 dark:border-amber-800"
                            : "hover:bg-muted/30",
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center gap-2.5 border-b px-3 py-2.5",
                        isSelected ? "bg-background" : "bg-muted/20",
                      )}
                    >
                      {firmId ? (
                        <GaMark id={firmId} name={email.title} />
                      ) : (
                        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted">
                          <Users className="h-3.5 w-3.5 text-muted-foreground" />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{email.title}</span>
                        <span
                          className={cn(
                            "block truncate text-[11px]",
                            goingNowhere
                              ? "text-amber-700 dark:text-amber-500"
                              : "text-muted-foreground",
                          )}
                        >
                          {goingNowhere
                            ? "No recipient — this email won't be sent"
                            : `1 email · ${email.ticked.length} recipient${
                                email.ticked.length === 1 ? "" : "s"
                              }${carrierNames.length ? ` · ${carrierNames.length} carriers` : ""}`}
                        </span>
                      </span>
                      {isSelected && (
                        <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-muted-foreground">
                          <Eye className="h-3 w-3" />
                          Previewing
                        </span>
                      )}
                    </div>

                    <div className="space-y-2.5 px-3 py-2.5">
                      <div>
                        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          Send to
                        </p>
                        <div className="space-y-1">
                          {email.candidates.map((contact) =>
                            contact.id.startsWith("custom-")
                              ? renderCustomRow(contact)
                              : renderCompactRow(contact),
                          )}
                        </div>
                        {(
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setNewContact({
                                name: "",
                                email: "",
                                carrierId: String(email.ticked[0]?.carrierId ?? ""),
                              });
                              setAddContactTab("new");
                              setAddContactOpen(true);
                            }}
                            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                          >
                            <Plus className="h-3 w-3" />
                            Add contact at {email.title.split(" ")[0]}
                          </button>
                        )}
                      </div>

                      {(askedByLine.length > 0 || untaggedNames.length > 0) && (
                        <div>
                          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Asked to quote
                          </p>
                          <div className="space-y-1">
                            {askedByLine.map((line) => {
                              const accent = getPlanTypeAccent(normalizePlanType(line.coverageType));
                              return (
                                <div key={line.coverageType} className="flex gap-2 text-xs">
                                  <span
                                    className={cn(
                                      "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase",
                                      accent.accentBg,
                                      accent.accentText,
                                    )}
                                  >
                                    {getCoverageLabel(line.coverageType)}
                                  </span>
                                  <span className="min-w-0 flex-1 leading-relaxed">
                                    {line.names.join(", ")}
                                  </span>
                                </div>
                              );
                            })}
                            {untaggedNames.length > 0 && (
                              <p className="text-xs leading-relaxed">{untaggedNames.join(", ")}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary */}
          {allRecipients.length > 0 && (
            <div className="mt-4 pt-4 border-t text-sm text-muted-foreground">
              <strong>{allRecipients.length}</strong> recipient
              {allRecipients.length !== 1 ? "s" : ""} — each sent their own email
              {/* "Asked to quote" is read-only on purpose: step 4 owns it. */}
              {usingGa && (
                <p className="mt-1 text-xs">
                  Carrier lists come from step 4. Go back to change them.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right: exactly what the selected email contains. Sticky, because the
          left column can be long and the preview is what is being checked. */}
      <Card className="lg:sticky lg:top-4">
        {/* Same mark and tint as the selected card on the left. */}
        <CardHeader
          className={cn(
            "flex-row items-center gap-2.5 space-y-0 rounded-t-xl border-b py-3",
            previewFirmId && toneFor(previewFirmId).soft,
          )}
        >
          {previewFirmId && selectedEmail ? (
            <GaMark id={previewFirmId} name={selectedEmail.title} />
          ) : (
            <Mail className="h-5 w-5 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base font-semibold">
              {selectedEmail ? `Email to ${selectedEmail.title}` : "Email preview"}
            </CardTitle>
            <p className="truncate text-xs text-muted-foreground">
              {selectedEmail
                ? `Exactly what ${selectedEmail.title} receives. Every recipient gets their own copy, greeted by name.`
                : "Pick an email on the left to see it."}
            </p>
          </div>
          {editingTemplate ? (
            <div className="flex shrink-0 gap-1.5">
              {/* Red: it throws away typing. */}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={!templateEdited}
                onClick={() => setTemplateDraft(templateBasis)}
              >
                Reset
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  // Text left exactly as ours is stored as null, not as a copy
                  // of it. A frozen copy would stop following the template if it
                  // ever changes, and would read as an edit nobody made.
                  const unchanged = (typed: string, ours: string) =>
                    typed.trim() === ours.trim() ? null : typed.trim() || null;
                  onTemplateChange({
                    respondBy:
                      templateDraft.respondBy === templateBasis.respondBy
                        ? respondBy
                        : templateDraft.respondBy || null,
                    emailGreeting: unchanged(templateDraft.greeting, templateBasis.greeting),
                    emailIntro: unchanged(templateDraft.intro, templateBasis.intro),
                    emailClosing: unchanged(templateDraft.closing, templateBasis.closing),
                    emailSignature: unchanged(templateDraft.signature, templateBasis.signature),
                  });
                  setEditing(false);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 text-xs"
              onClick={() => {
                const ours = {
                  respondBy: format(defaultRespondBy(), "yyyy-MM-dd"),
                  greeting: generatedContent.salutation,
                  intro: generatedContent.intro,
                  closing: generatedContent.closing.join("\n\n"),
                  // One line each: a sign-off is short lines, not paragraphs.
                  signature: generatedContent.signature.join("\n"),
                };
                setTemplateBasis(ours);
                // Prefilled with the wording in use: changing one word should
                // not mean retyping the paragraph.
                setTemplateDraft({
                  respondBy: respondBy || ours.respondBy,
                  greeting: emailGreeting ?? ours.greeting,
                  intro: emailIntro ?? ours.intro,
                  closing: emailClosing ?? ours.closing,
                  signature: emailSignature ?? ours.signature,
                });
                setEditing(true);
              }}
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit message
            </Button>
          )}
        </CardHeader>
        {/* CardContent ships pt-0, which assumed a transparent header. The
            tinted one needs padding or the From row sits on the colour band. */}
        <CardContent className="space-y-4 pt-6">
          {/* The envelope, in the order a mail client shows it. */}
          <div className="rounded-lg border bg-muted/30 divide-y">
            <div className="flex items-baseline gap-3 px-4 py-2.5">
              <span className="text-xs text-muted-foreground w-20 shrink-0">From</span>
              <div className="min-w-0">
                <p className="text-sm truncate">
                  {senderName ? `${senderName} · ` : ""}
                  <span className="font-mono text-xs">{senderEmail}</span>
                </p>
                <p className="text-[11px] text-muted-foreground">Your agency's sending address</p>
              </div>
            </div>

            <div className="flex items-baseline gap-3 px-4 py-2.5">
              <Label htmlFor="rfp-reply-to" className="text-xs text-muted-foreground w-20 shrink-0">
                Reply to
              </Label>
              <div className="flex-1 min-w-0">
                <Input
                  id="rfp-reply-to"
                  className="h-7 text-sm font-mono"
                  value={replyTo}
                  onChange={(e) => onReplyToChange(e.target.value)}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Carrier replies come straight to this address.
                </p>
              </div>
            </div>

            <div className="flex items-baseline gap-3 px-4 py-2.5">
              <span className="text-xs text-muted-foreground w-20 shrink-0">Subject</span>
              {/* From the builder, not retyped: the subject used to be
                  hardcoded here, so any change to it never showed. */}
              <p className="text-sm font-medium min-w-0 truncate" title={previewContent.subject}>
                {previewContent.subject}
              </p>
            </div>

            <div className="flex items-baseline gap-3 px-4 py-2.5">
              <span className="text-xs text-muted-foreground w-20 shrink-0">To</span>
              <div className="min-w-0 flex-1">
                {!selectedEmail || selectedEmail.ticked.length === 0 ? (
                  <p className="text-sm text-amber-700 dark:text-amber-500">
                    Nobody — tick a contact on the left.
                  </p>
                ) : (
                  <>
                    {/* Everyone on this email, and which copy is being shown.
                        This replaced the preview tabs: with two contacts at one
                        GA they carried the same label and could not be told
                        apart. */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {selectedEmail.ticked.map((recipient) => {
                        const isPreviewing = recipient.id === previewRecipient?.id;
                        return (
                          <button
                            key={recipient.id}
                            type="button"
                            onClick={() => setPreviewRecipientId(recipient.id)}
                            title={recipient.email}
                            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                              isPreviewing
                                ? "border-primary bg-primary/10 font-medium"
                                : "hover:bg-muted"
                            }`}
                          >
                            {recipient.name}
                            {perRecipientMessages[recipient.id]?.trim() && (
                              <span className="ml-1 opacity-70">✎</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {selectedEmail.ticked.length > 1 && (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Each gets their own copy, greeted by name. Click one to see theirs.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* A real Cc on every one of these emails, not an email of their
                own. The carrier sees these addresses. */}
            {ccRecipients.length > 0 && (
              <div className="flex items-baseline gap-3 px-4 py-2.5">
                <span className="text-xs text-muted-foreground w-20 shrink-0">Cc</span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {ccRecipients.map((recipient) => (
                      <span
                        key={recipient.id}
                        title={recipient.email}
                        className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs dark:border-blue-800 dark:bg-blue-950"
                      >
                        {recipient.name}
                        <button
                          type="button"
                          onClick={() => onRemoveCustomRecipient(recipient.id)}
                          className="text-muted-foreground hover:text-destructive"
                          aria-label={`Remove ${recipient.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    On every email that goes out. Carriers can see them.
                  </p>
                </div>
              </div>
            )}

            {/* With From, Reply to, Subject and To: these are the decisions
                made ABOUT the email, while the body below is what it says. */}
            {attachmentsSlot && (
              <div className="flex items-baseline gap-3 px-4 py-2.5">
                <span className="text-xs text-muted-foreground w-20 shrink-0">Attached</span>
                <div className="min-w-0 flex-1">{attachmentsSlot}</div>
              </div>
            )}
          </div>

          {/* Email Body */}
          <div>
            {/* Ringed while editing: half this box turns into fields and half
                stays read-only text. */}
            <div
              className={cn(
                "prose prose-sm dark:prose-invert max-w-none overflow-hidden border rounded-lg bg-background transition-colors",
                editingTemplate &&
                  "border-blue-300 ring-2 ring-blue-500/15 dark:border-blue-800",
              )}
            >
              {/* The agency's own mark, where the Administr wordmark sits in
                  every other email. A data: URI is fine in a browser; the send
                  turns it into an embedded attachment, since mail clients
                  strip these. */}
              {agencyLogo && (
                <div className="not-prose border-b bg-white px-4 py-4 text-center">
                  <img
                    src={agencyLogo}
                    alt={sender?.agencyName ?? "Agency"}
                    className="mx-auto max-h-12 max-w-[220px] object-contain"
                  />
                </div>
              )}
              <div className="p-4">
              {/* These fields sit inside a card titled "Email to <firm>", so
                  without this line an edit reads as belonging to that one
                  firm when it belongs to all of them. */}
              {editingTemplate && (
                <p className="not-prose mb-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-900 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-200">
                  Editing the message for{" "}
                  <strong>
                    all {outgoingEmails.length} email{outgoingEmails.length === 1 ? "" : "s"}
                  </strong>{" "}
                  on this RFP. For wording only one firm sees, use Additional notes below.
                </p>
              )}

              {/* The word is editable, the name is not: this paragraph is
                  "Dear " + whoever this copy goes to. */}
              {editingTemplate ? (
                <p className="not-prose flex items-center gap-2">
                  <Input
                    aria-label="Greeting"
                    className="h-8 w-28 text-sm"
                    value={templateDraft.greeting}
                    onChange={(e) =>
                      setTemplateDraft((draft) => ({ ...draft, greeting: e.target.value }))
                    }
                  />
                  <span className="text-sm">
                    {previewRecipient?.name?.split(" ")[0] || "[First Name]"},
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    each recipient's own name
                  </span>
                </p>
              ) : (
                <p>
                  {previewContent.salutation}{" "}
                  {previewRecipient?.name?.split(" ")[0] || "[First Name]"},
                </p>
              )}

              {/* From the builder, not typed out again here: written twice, the
                  screen and the email drift, and the carrier row never showed. */}
              {editingTemplate ? (
                <Textarea
                  aria-label="Opening paragraph"
                  className="not-prose min-h-[72px] text-sm"
                  placeholder={generatedContent.intro}
                  value={templateDraft.intro}
                  onChange={(e) =>
                    setTemplateDraft((draft) => ({ ...draft, intro: e.target.value }))
                  }
                />
              ) : (
                <p className="whitespace-pre-line">{previewContent.intro}</p>
              )}

              {/* The same two-column block the email renders. */}
              <table className="not-prose my-5 w-full border-collapse rounded-md border bg-muted/20">
                <tbody>
                  {previewContent.facts.map((fact) => {
                    // The deadline is the one fact the broker sets, so in edit
                    // mode that row's value becomes the date field, in place.
                    const editable = editingTemplate && fact.label === "Quotes due";
                    return (
                      <tr key={fact.label}>
                        <td className="w-2/5 whitespace-nowrap px-4 py-1.5 align-top text-sm text-muted-foreground first:pt-3.5 last:pb-3.5">
                          {fact.label}
                        </td>
                        <td className="px-4 py-1.5 text-sm font-semibold">
                          {editable ? (
                            <Input
                              type="date"
                              aria-label="Quotes due"
                              className="h-7 w-auto text-sm"
                              value={templateDraft.respondBy}
                              onChange={(e) =>
                                setTemplateDraft((draft) => ({
                                  ...draft,
                                  respondBy: e.target.value,
                                }))
                              }
                            />
                          ) : (
                            fact.value
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Coverage Requirements by Type */}
              <h2 className="not-prose mb-0 mt-8 text-lg font-semibold">
                Coverage requirements
                {editingTemplate && (
                  <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                    from step 3 · each recipient sees only the lines they were asked for
                  </span>
                )}
              </h2>

              {previewContent.sections.map((section) => (
                <div key={section.coverageType}>
                  {/* Same small tracked label the email uses. */}
                  <h3 className="not-prose mb-1.5 mt-6 text-[13px] font-bold uppercase tracking-wider">
                    {section.label}
                  </h3>
                  <table className="not-prose w-full border-collapse text-sm">
                    <tbody>
                      {section.items.map((item, index) => (
                        <tr key={item.label} className={index === 0 ? "" : "border-t"}>
                          <td className="w-2/5 whitespace-nowrap py-1.5 pr-4 align-top text-muted-foreground">
                            {item.label}
                          </td>
                          <td
                            className={`py-1.5 ${item.value === NOT_SPECIFIED ? "text-amber-700 dark:text-amber-500" : ""}`}
                          >
                            {item.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              {/* Edited where it appears, so there's no guessing where the note
                  lands in the email. Writes to the shared message by default, or
                  to a note only the previewed carrier sees. */}
              <Separator className="my-4" />
              <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 p-4 rounded-lg not-prose">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="font-medium text-sm">Additional notes</p>
                  {previewRecipient && (
                    <div className="flex rounded-md border border-blue-300 dark:border-blue-700 overflow-hidden text-[11px]">
                      <button
                        type="button"
                        onClick={() => setMessageScope("shared")}
                        className={`px-2 py-0.5 ${
                          messageScope === "shared"
                            ? "bg-blue-600 text-white"
                            : "hover:bg-blue-100 dark:hover:bg-blue-900"
                        }`}
                      >
                        Everyone
                      </button>
                      <button
                        type="button"
                        onClick={() => setMessageScope("recipient")}
                        className={`px-2 py-0.5 ${
                          messageScope === "recipient"
                            ? "bg-blue-600 text-white"
                            : "hover:bg-blue-100 dark:hover:bg-blue-900"
                        }`}
                      >
                        Just {previewRecipient.carrierName || previewRecipient.name}
                      </button>
                    </div>
                  )}
                </div>

                <Textarea
                  className="min-h-[80px] bg-background text-sm"
                  placeholder={
                    messageScope === "recipient" && previewRecipient
                      ? `A note only ${previewRecipient.carrierName || previewRecipient.name} will see...`
                      : usingGa
                        ? "Optional — a note every GA will see..."
                        : "Optional — a note every carrier will see..."
                  }
                  value={
                    messageScope === "recipient" && previewRecipient
                      ? perRecipientMessages[previewRecipient.id] ?? ""
                      : customMessage
                  }
                  onChange={(e) =>
                    messageScope === "recipient" && previewRecipient
                      ? onPerRecipientMessageChange(previewRecipient.id, e.target.value)
                      : onCustomMessageChange(e.target.value)
                  }
                />

                {messageScope === "recipient" && previewRecipient && (
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Replaces the shared note for {previewRecipient.carrierName || previewRecipient.name}.
                    {!perRecipientMessages[previewRecipient.id]?.trim() && customMessage.trim() &&
                      " Leave empty and they'll get the shared one."}
                  </p>
                )}
              </div>

              <Separator className="my-4" />

              {/* From the builder, like the intro and subject: retyped here,
                  the screen and the email drift. */}
              {editingTemplate ? (
                <>
                  <Textarea
                    aria-label="Closing paragraphs"
                    className="not-prose min-h-[96px] text-sm"
                    placeholder={generatedContent.closing.join("\n\n")}
                    value={templateDraft.closing}
                    onChange={(e) =>
                      setTemplateDraft((draft) => ({ ...draft, closing: e.target.value }))
                    }
                  />
                  <p className="not-prose mt-1.5 text-[11px] text-muted-foreground">
                    Leave an empty line to start a new paragraph.
                  </p>
                </>
              ) : (
                previewContent.closing.map((paragraph) => (
                  <p key={paragraph} className="whitespace-pre-line">
                    {paragraph}
                  </p>
                ))
              )}

              {/* Editable like the prose, but one line each. */}
              {editingTemplate ? (
                <Textarea
                  aria-label="Signature"
                  className="not-prose mt-4 min-h-[76px] text-sm"
                  placeholder={generatedContent.signature.join("\n")}
                  value={templateDraft.signature}
                  onChange={(e) =>
                    setTemplateDraft((draft) => ({ ...draft, signature: e.target.value }))
                  }
                />
              ) : (
                previewContent.signature.length > 0 && (
                  <div className="not-prose mt-6">
                    {previewContent.signature.map((line, index) => (
                      <p
                        key={line}
                        className={cn(
                          "text-sm leading-relaxed",
                          index === 0 ? "font-semibold" : "text-muted-foreground",
                        )}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                )
              )}
              </div>
            </div>
          </div>

          <p className="text-muted-foreground text-xs">
            Each recipient will receive a unique email with a personalized greeting using their first name.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
